import React, { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { noop, isEmpty, compact } from 'underscore';
import classNames from 'classnames';

import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { useContentSuggestions, ContentSuggestionOption } from '@bingads-webui-campaign-react/smart-page-data-service';
import { Stack, SearchBox } from '@fluentui/react';
import { Loading } from '@bingads-webui-campaign-react/smart-component-primitive';
import { useIsM365Premium, useMobileSupportEnabled } from '@bingads-webui-campaign-react/smart-page-context';
import {
  getStockImageThumbnailUrl,
  getStockImageOriginalUrl,
  disableImportedImages,
  getUrlFromStockImage,
  ImageSource,
  getPathFromUrl,
} from '@bingads-webui-campaign-react/smart-component-util';

import { SelectImagesListView } from './select-images-list-view';
import { ImageItemView } from './image-item-view';
import { usePivotImageEditorContext } from '../smart-pivot-image-editor-context';

const getStyles = () => ({
  keywordInput: {
    maxWidth: '435px',
  },
  container: {
    margin: '25px 0 10px',
    position: 'relative',
  },
  loading: {
    height: '430px',
    backgroundColor: 'transparent',
  },
  liveMessage: {
    position: 'absolute',
    top: '0',
    fontSize: 10,
    zIndex: -1,
  },
});

const onRenderSuggestedImageItem = item => (
  <ImageItemView src={item.externalSrc} showLoading />
);

export const UnStyledSelectImagesListViewWithSearch = ({
  classes,
  defaultKeywords,
  onItemSelectionChanged,
  onSelectionChanged,
  imageWidth,
  imageHeight,
  pageHeight,
  i18n,
  isMultiple,
  itemsPerPage,
  className,
  shouldResetSelection,
  existingImages,
  itemToUnselect,
  resetItemToUnselect,
}) => {
  const [viewItems, setViewItems] = useState([]);
  const [searchKeywordsInput, setSearchKeywordsInput] = useState((defaultKeywords || []).join(', '));
  const searchKeywords = useMemo(() => compact(searchKeywordsInput.split(',').map(kw => kw.trim())), [searchKeywordsInput]);

  const isMobile = useMobileSupportEnabled();
  const isM365Premium = useIsM365Premium();
  const { selectionIds } = usePivotImageEditorContext();
  const itemToUnselectInternal = itemToUnselect ? { ...itemToUnselect } : null;
  // If the item is stock image, we change its url to thumbnail (required by child)
  if (itemToUnselectInternal && getUrlFromStockImage(itemToUnselectInternal)) {
    itemToUnselectInternal.externalSrc = getStockImageThumbnailUrl(itemToUnselectInternal);
  }

  const { imageSuggestions, isLoading } = useContentSuggestions(
    null /* biz name */,
    searchKeywords,
    ContentSuggestionOption.ImageOnly,
    !isEmpty(searchKeywords) /* initWithLoading, so we won't show no suggestion in the first load */
  );

  const noTipsMessage = isM365Premium
    ? _TL_('No image suggestions, please input a keyword or enter a new one.')
    : _TL_('No image suggestions.');

  const searchResultMessage = viewItems.length > 0
    ? i18n.getString(_TL_('{{number}} images suggestion available for {{searchKey}}'), { number: viewItems.length, searchKey: searchKeywordsInput })
    : i18n.getString(noTipsMessage);

  // We passed down the src with thumbnail, when bubble up, need to restore it
  const handleResetItemToUnselect = useCallback((item) => {
    const itemToReset = {
      ...item,
      externalSrc: getStockImageOriginalUrl(item),
    };
    resetItemToUnselect(itemToReset);
  }, [resetItemToUnselect]);

  const handleSearchQueryChange = useCallback((e, value) => {
    setSearchKeywordsInput(value || '');
  }, []);

  const handleOnSelectionChanged = useCallback((items) => {
    const finalItems = items.map(item => ({
      ...item,
      externalSrc: getStockImageOriginalUrl(item),
    }));
    onSelectionChanged(finalItems);
  }, [onSelectionChanged]);

  const handleOnItemSelectionChanged = useCallback((item) => {
    const finalItem = {
      ...item,
      externalSrc: getStockImageOriginalUrl(item),
    };
    onItemSelectionChanged(finalItem);
  }, [onItemSelectionChanged]);

  useEffect(() => {
    if (imageSuggestions) {
      const finalSuggestions = disableImportedImages(imageSuggestions.map(item => ({
        externalSrc: item.ImageUrl,
        source: item.ImageSource,
        id: getPathFromUrl(item.ImageUrl),
      })), existingImages, ImageSource.StockImage);

      const suggestionsViewItems = finalSuggestions.map(sug => ({
        ...sug,
        externalSrc: getStockImageThumbnailUrl(sug),
        selected: selectionIds.includes(sug.id),
      }));

      setViewItems(suggestionsViewItems);
    } else {
      setViewItems([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageSuggestions, existingImages]);

  return (
    <React.Fragment>
      <div className={classNames('select-images-list-view-with-search', classes.container, className)}>
        <Stack tokens={{ childrenGap: '10px' }}>
          <SearchBox
            className={classNames('image-suggestion-search', classes.keywordInput)}
            value={searchKeywordsInput}
            onChange={handleSearchQueryChange}
            placeholder={i18n.getString(_TL_('Search images'))}
            ariaLabel={i18n.getString(_TL_('Search images'))}
            showIcon
          />
          <Stack.Item grow>
            {
              isLoading ? <Loading className={classes.loading} />
                : <SelectImagesListView
                  items={viewItems}
                  // Tmp solution for infinite vertical rendering
                  // TODO dynamically set width base on container
                  pageHeight={isMobile ? 9999 : pageHeight}
                  imageWidth={isMobile ? 155 : imageWidth}
                  imageHeight={isMobile ? 120 : imageHeight}
                  onRenderImageItem={onRenderSuggestedImageItem}
                  onItemSelectionChanged={handleOnItemSelectionChanged}
                  onSelectionChanged={handleOnSelectionChanged}
                  selectable
                  isMultiple={isMultiple}
                  i18n={i18n}
                  itemsPerPage={itemsPerPage}
                  showPagination={!isMobile}
                  shouldResetSelection={shouldResetSelection}
                  itemToUnselect={itemToUnselectInternal}
                  resetItemToUnselect={handleResetItemToUnselect}
                  noImageTips={noTipsMessage}
                />
            }
          </Stack.Item>
          <div role="region" aria-live="polite" className={classes.liveMessage}>
            <span>{searchResultMessage}</span>
          </div>
        </Stack>
      </div>
    </React.Fragment>
  );
};

UnStyledSelectImagesListViewWithSearch.propTypes = {
  classes: PropTypes.shape({}).isRequired,
  imageWidth: PropTypes.number.isRequired,
  imageHeight: PropTypes.number.isRequired,
  pageHeight: PropTypes.number.isRequired,
  onItemSelectionChanged: PropTypes.func,
  onSelectionChanged: PropTypes.func,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  isMultiple: PropTypes.bool,
  defaultKeywords: PropTypes.arrayOf(PropTypes.string),
  itemsPerPage: PropTypes.number,
  className: PropTypes.string,
  shouldResetSelection: PropTypes.bool,
  existingImages: PropTypes.arrayOf(PropTypes.any),
  itemToUnselect: PropTypes.shape({}),
  resetItemToUnselect: PropTypes.func,
};

UnStyledSelectImagesListViewWithSearch.defaultProps = {
  onItemSelectionChanged: noop,
  onSelectionChanged: noop,
  isMultiple: false,
  defaultKeywords: [],
  itemsPerPage: 6,
  className: null,
  shouldResetSelection: false,
  existingImages: [],
  itemToUnselect: null,
  resetItemToUnselect: noop,
};

export const SelectImagesListViewWithSearch = withDefaultStyles(
  UnStyledSelectImagesListViewWithSearch,
  getStyles
);
